type DeepReadonly<T> = {
  readonly [K in keyof T]: T[K] extends object ? DeepReadonly<T[K]> : T[K];
};

export interface WizardConfig {
  education: {
    baseCollegeCost: number;
    baseTradeschoolCost: number;
    inflationRate: number;
    baselineYear: number;
  };
  eligibility: {
    minimumAge: number;
    minEducationAge: number;
  };
  contributions: {
    paychecksPerYear: number;
    contributionTolerance: number;
  };
}

const defaults: WizardConfig = {
  education: {
    baseCollegeCost: 145000,
    baseTradeschoolCost: 35976,
    inflationRate: 1.02,
    baselineYear: 2025,
  },
  eligibility: {
    minimumAge: 18,
    minEducationAge: 18,
  },
  contributions: {
    paychecksPerYear: 26,
    contributionTolerance: 0.02,
  },
};

const FETCH_TIMEOUT_MS = 3000;

const isPositiveNumber = (v: unknown): v is number =>
  typeof v === "number" && isFinite(v) && v > 0;

const isNonNegativeNumber = (v: unknown): v is number =>
  typeof v === "number" && isFinite(v) && v >= 0;

const sanitize = (json: Record<string, unknown>): WizardConfig => {
  const edu = (json.education ?? {}) as Record<string, unknown>;
  const elig = (json.eligibility ?? {}) as Record<string, unknown>;
  const contrib = (json.contributions ?? {}) as Record<string, unknown>;

  return {
    education: {
      baseCollegeCost: isPositiveNumber(edu.baseCollegeCost)
        ? edu.baseCollegeCost
        : defaults.education.baseCollegeCost,
      baseTradeschoolCost: isPositiveNumber(edu.baseTradeschoolCost)
        ? edu.baseTradeschoolCost
        : defaults.education.baseTradeschoolCost,
      inflationRate: isPositiveNumber(edu.inflationRate)
        ? edu.inflationRate
        : defaults.education.inflationRate,
      baselineYear: isPositiveNumber(edu.baselineYear)
        ? edu.baselineYear
        : defaults.education.baselineYear,
    },
    eligibility: {
      minimumAge: isPositiveNumber(elig.minimumAge)
        ? elig.minimumAge
        : defaults.eligibility.minimumAge,
      minEducationAge: isPositiveNumber(elig.minEducationAge)
        ? elig.minEducationAge
        : defaults.eligibility.minEducationAge,
    },
    contributions: {
      paychecksPerYear: isPositiveNumber(contrib.paychecksPerYear)
        ? contrib.paychecksPerYear
        : defaults.contributions.paychecksPerYear,
      contributionTolerance: isNonNegativeNumber(contrib.contributionTolerance)
        ? contrib.contributionTolerance
        : defaults.contributions.contributionTolerance,
    },
  };
};

const deepFreeze = (obj: WizardConfig): DeepReadonly<WizardConfig> => {
  Object.values(obj).forEach((value) => Object.freeze(value));
  return Object.freeze(obj) as DeepReadonly<WizardConfig>;
};

let config: DeepReadonly<WizardConfig> = deepFreeze({
  education: { ...defaults.education },
  eligibility: { ...defaults.eligibility },
  contributions: { ...defaults.contributions },
});

export const initWizardConfig = async (): Promise<void> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${import.meta.env.BASE_URL}wizardConfig.json`, {
      signal: controller.signal,
    });
    if (response.ok) {
      const json = await response.json();
      config = deepFreeze(sanitize(json));
    }
  } catch {
    // fall back to defaults
  } finally {
    clearTimeout(timeout);
  }
};

export const getWizardConfig = (): DeepReadonly<WizardConfig> => config;
